import * as React from 'react';

/**
 * The translator contract and its context, in a module with NO app imports.
 *
 * Kept separate from `i18n-provider.tsx` on purpose: the provider pulls in `auth`, `api` and `layout`, and
 * this module is what the design system (`src/ui`) and the test / Storybook wrappers import. Splitting it
 * keeps the app scaffolding out of those bundles and avoids a ui -> i18n -> layout -> ui import cycle.
 */
export type Translator = (key: string, tokens?: { [key: string]: string }, forceEnglish?: boolean) => string;

export interface I18nContextShape {
    __: Translator;
}

/**
 * Builds a translator over one flat `module.key` -> string map. Used by the app provider (with the
 * react-query-loaded locale), by Storybook and by the unit-test wrapper (both with the bundled English).
 *
 * A missing key comes back AS the key: ugly, but visible and greppable, where an empty string is a silent
 * hole in the UI. Token replacement uses the FUNCTION form of `replace` because token values are customer
 * data, and a string replacement would interpret `$&`, `$'` and `$1` inside them as patterns.
 */
export const createTranslator =
    (translations: { [key: string]: string } | undefined) =>
    (key: string, tokens?: { [key: string]: string }): string => {
        let translation = translations?.[key] || key;

        if (tokens) {
            Object.keys(tokens).forEach((token) => {
                translation = translation.replace(new RegExp(`:${token}`, 'ig'), () => tokens[token]);
            });
        }

        return translation;
    };

export const I18nContext = React.createContext<I18nContextShape>({
    __: (): string => process.env.REACT_APP_TRANSLATIONS_NOT_LOADED_STR || '',
});
